import {
	AnnotationEditorParamsType,
	AnnotationEditorType
} from '@denodecom/pdfjs-dist/types';
import { createSlice } from '@reduxjs/toolkit';

import { CursorTool } from '@/pdfjsWeb/constants';

import {
	handleAsyncThunkActionState,
	initialAsyncState,
	isAsyncThunkActionWithPrefix
} from '../utils';
import { fetchNotes, fetchTransaction, removeNote, saveNote } from './thunks';

export const SHARE_MODAL_TYPES = {
	COPY_LINK: 'copy-link',
	PRINT: 'print',
	SEND_BY_EMAIL: 'send-by-email'
};

export const KEYS_QUALITY = {
	HIGH: 'high',
	MEDIUM: 'medium',
	LOW: 'low'
};

const initialState = {
	compressedFile: {
		[KEYS_QUALITY.HIGH]: null,
		[KEYS_QUALITY.MEDIUM]: null,
		[KEYS_QUALITY.LOW]: null
	},
	renderKey: 0,
	filePath: null,
	metadata: null,
	status: null,
	fetchTransaction: initialAsyncState,
	fetchSignatures: initialAsyncState,
	isModalCompressOpen: false,
	isModalEditNameOpen: false,
	lastSaved: {
		hash: '',
		pageOrder: null,
		pageRotation: null,
		fileOpenCount: 0
	},
	progress: 0,
	pdfUrl: null,
	initialized: false,
	pageNumber: 1,
	pagesCount: 0,
	viewPagesCount: 0,
	pageOrder: [],
	scale: 1,
	presetValue: null,
	cursorTool: CursorTool.SELECT,
	cursorToolDisabled: false,
	annotationEditMode: 0,
	annotationEditorSelected: AnnotationEditorType.ANY,
	password: '',
	protectModalOpen: false,
	shareModalOpen: false,
	shareModalType: SHARE_MODAL_TYPES.SEND_BY_EMAIL,
	signatureModalOpen: false,
	signatureFontsLoaded: false,
	pagesModalOpen: false,
	joinModalOpen: false,
	annotationEditorStates: {
		hasSelectedEditor: false,
		hasSelectedText: false,
		hasSomethingToUndo: false,
		hasSomethingToRedo: false,
		isEditing: false,
		isEmpty: false
	},
	annotationEditorParams: {
		highlightColor: '#FFFF98',
		highlightThickness: 12,
		freeTextColor: '#000000',
		freeTextSize: 13,
		textColor: '#000000',
		textFont: 'Helvetica',
		textSize: 13,
		textAlign: 'left',
		textBold: false,
		textItalic: false,
		textUnderline: false,
		textStrikethrough: false,
		textList: 'none',
		textBackgroundColor: 'transparent',
		inkColor: '#000000',
		inkThickness: 1,
		inkOpacity: 1,
		shapeThickness: 1,
		shapeColor: '#000000',
		shapeFillColor: 'none',
		highlightShowAll: true,
		eraserColor: '#FFFFFF',
		opacity: 1,
		transparentFill: true,
		transparentColor: false
	},
	notes: [],
	notesSearchTerm: '',
	clickedNoteInPage: null,
	addNoteMobileMode: false,
	clickedNewNoteInPage: false,
	customMode: null,
	isToolsUntouched: true,
	pendingEditMode: null,
	newNote: null,
	editingNoteId: null,
	savingNoteId: null,
	fetchingNotes: false,
	saveAlertOpen: false,
	hasText: null,
	embeddedFonts: [],
	triggerAction: '',
	recoveryTransactionId: null,
	allFontsLoaded: false
};

const editorParamsMapping = {
	[AnnotationEditorParamsType.HIGHLIGHT_DEFAULT_COLOR]: 'highlightColor',
	[AnnotationEditorParamsType.HIGHLIGHT_THICKNESS]: 'highlightThickness',
	[AnnotationEditorParamsType.FREETEXT_COLOR]: 'freeTextColor',
	[AnnotationEditorParamsType.FREETEXT_SIZE]: 'freeTextSize',
	[AnnotationEditorParamsType.TEXT_FONT]: 'textFont',
	[AnnotationEditorParamsType.TEXT_CONTENT_COLOR]: 'textColor',
	[AnnotationEditorParamsType.TEXT_CONTENT_SIZE]: 'textSize',
	[AnnotationEditorParamsType.TEXT_ALIGN]: 'textAlign',
	[AnnotationEditorParamsType.TEXT_BOLD]: 'textBold',
	[AnnotationEditorParamsType.TEXT_ITALIC]: 'textItalic',
	[AnnotationEditorParamsType.TEXT_UNDERLINE]: 'textUnderline',
	[AnnotationEditorParamsType.TEXT_STRIKETHROUGH]: 'textStrikethrough',
	[AnnotationEditorParamsType.TEXT_LIST]: 'textList',
	[AnnotationEditorParamsType.TEXT_BACKGROUND_COLOR]: 'textBackgroundColor',
	[AnnotationEditorParamsType.INK_COLOR]: 'inkColor',
	[AnnotationEditorParamsType.INK_THICKNESS]: 'inkThickness',
	[AnnotationEditorParamsType.INK_OPACITY]: 'inkOpacity',
	[AnnotationEditorParamsType.SHAPE_COLOR]: 'shapeColor',
	[AnnotationEditorParamsType.SHAPE_FILL_COLOR]: 'shapeFillColor',
	[AnnotationEditorParamsType.SHAPE_THICKNESS]: 'shapeThickness',
	[AnnotationEditorParamsType.HIGHLIGHT_SHOW_ALL]: 'highlightShowAll',
	[AnnotationEditorParamsType.ERASER_COLOR]: 'eraserColor',
	[AnnotationEditorParamsType.OPACITY]: 'opacity',
	[AnnotationEditorParamsType.SHAPE_TRANSPARENT_COLOR]: 'transparentColor',
	[AnnotationEditorParamsType.SHAPE_TRANSPARENT_FILL]: 'transparentFill'
};

const editorSlice = createSlice(
	{
		name: 'editor',
		initialState,
		reducers: {
			init(state, action) {
				const { pagesCount, viewPagesCount, pageOrder } = action.payload;

				state.initialized = true;
				state.pagesCount = pagesCount;
				state.viewPagesCount = viewPagesCount;
				state.pageOrder = pageOrder;
			},
			clear(state) {
				return {
					...initialState,
					signatureFontsLoaded: state.signatureFontsLoaded
				};
			},
			reset(state) {
				return {
					...initialState,
					renderKey: state.renderKey + 1,
					filePath: state.filePath,
					metadata: state.metadata,
					fetchTransaction: state.fetchTransaction,
					password: state.password,
					signatureFontsLoaded: state.signatureFontsLoaded,
					lastSaved: state.lastSaved
				};
			},
			resetCompressedFile(state) {
				state.compressedFile = {
					[KEYS_QUALITY.HIGH]: null,
					[KEYS_QUALITY.MEDIUM]: null,
					[KEYS_QUALITY.LOW]: null
				};
			},
			setCompressedFile(state, action) {
				const { file, quality } = action.payload;
				state.compressedFile[quality] = file;
			},
			setIsModalEditNameOpen(state, action) {
				state.isModalEditNameOpen = action.payload;
			},
			setIsToolsUntouched(state, action) {
				state.isToolsUntouched = action.payload;
			},
			setLastSaved(state, action) {
				state.lastSaved = action.payload;
			},
			setOriginalFileName(state, action) {
				state.metadata.originalFileName = action.payload;
			},
			setPageNumber(state, action) {
				state.pageNumber = action.payload;
			},
			setScale(state, action) {
				const { scale, presetValue } = action.payload;
				state.scale = scale;
				state.presetValue = presetValue;
			},
			setProgress(state, action) {
				state.progress = action.payload;
			},
			setPdfUrl(state, action) {
				state.pdfUrl = action.payload;
			},
			setToolbarOption(state, action) {
				const { name, value } = action.payload;
				state.toolbar[name] = value;
			},
			setAnnotationEditMode(state, action) {
				state.annotationEditMode = action.payload;
			},
			setAnnotationEditorSelected(state, action) {
				state.annotationEditorSelected = action.payload;
			},
			setAnnotationEditorParam(state, action) {
				const { type, value } = action.payload;
				const parsedName = editorParamsMapping[type];
				if (parsedName) {
					state.annotationEditorParams[parsedName] = value;
				}
			},
			setAnnotationEditorParams(state, action) {
				action.payload.forEach(([type, value]) => {
					const parsedName = editorParamsMapping[type];
					if (parsedName) {
						state.annotationEditorParams[parsedName] = value;
					}
				});
			},
			setCursorTool(state, action) {
				const { tool, disabled } = action.payload;
				state.cursorTool = tool;
				state.cursorToolDisabled = disabled;
			},
			setSidebarOptionDisabled(state, action) {
				const { name, value } = action.payload;
				state.sidebarOptionsDisabled[name] = value;
			},
			setAnnotationEditorStates(state, action) {
				state.annotationEditorStates = action.payload;
			},
			setShareModalOpen(state, action) {
				state.shareModalOpen = action.payload;
			},
			setShareModalType(state, action) {
				state.shareModalType = action.payload;
			},
			setIsModalCompressOpen(state, action) {
				state.isModalCompressOpen = action.payload;
			},
			setProtectModalOpen(state, action) {
				state.protectModalOpen = action.payload;
			},
			setPassword(state, action) {
				state.password = action.payload;
			},
			setSignatureModalOpen(state, action) {
				state.signatureModalOpen = action.payload;
			},
			setSignatureFontsLoaded(state) {
				state.signatureFontsLoaded = true;
			},
			setPageOrder(state, action) {
				const { pageOrder, viewPagesCount, pagesCount } = action.payload;

				state.pageOrder = pageOrder;
				state.viewPagesCount = viewPagesCount;
				state.pagesCount = pagesCount;
			},
			setPagesModalOpen(state, action) {
				state.pagesModalOpen = action.payload;
			},
			setJoinModalOpen(state, action) {
				state.joinModalOpen = action.payload;
			},
			putNewEmptyNote(state, action) {
				state.newNote = action.payload;
			},
			removeNewEmptyNote(state) {
				state.newNote = null;
			},
			addNote(state, action) {
				state.notes = [...state.notes, action.payload];
			},
			removeNoteFromStore(state, action) {
				state.notes = state.notes.filter(
					note => note.id !== action.payload
				);
			},
			setNoteSearchTerm(state, action) {
				state.notesSearchTerm = action.payload;
			},
			setNoteForEdition(state, action) {
				state.editingNoteId = action.payload;
			},
			undoEditingNote(state) {
				state.editingNoteId = null;
			},
			setSavingNote(state, action) {
				state.savingNoteId = action.payload;
			},
			setClickedNoteInPage(state, action) {
				state.clickedNoteInPage = action.payload;
			},
			setAddNoteMobileMode(state, action) {
				state.addNoteMobileMode = action.payload;
			},
			setClickedNewNoteInPage(state, action) {
				state.clickedNewNoteInPage = action.payload;
			},
			switchClickedNewNoteInPage(state) {
				state.clickedNewNoteInPage = !state.clickedNewNoteInPage;
			},
			resetMobileNoteState(state) {
				state.clickedNewNoteInPage = false;
				state.addNoteMobileMode = false;
				state.notes = state.notes.filter(note => note.id !== null);
			},
			setCustomMode(state, action) {
				state.customMode = action.payload;
			},
			setSaveAlert(state, action) {
				const { open, mode } = action.payload;
				state.pendingEditMode = mode || null;
				state.saveAlertOpen = open;
			},
			setHasText(state, action) {
				state.hasText = action.payload;
			},
			addEmbeddedFont(state, action) {
				// TODO: Check why fonts are added multiple times
				// when a new document is opened
				if (
					state.embeddedFonts.some(
						font => font.value === action.payload.value
					)
				) {
					return;
				}
				state.embeddedFonts.push(action.payload);
			},
			setTriggerAction(state, action) {
				state.triggerAction = action.payload;
			},
			setRecoveryId(state, action) {
				state.recoveryTransactionId = action.payload;
			},
			setAllFontsLoaded(state, action) {
				state.allFontsLoaded = action.payload;
			}
		},
		extraReducers(builder) {
			builder.addCase(fetchTransaction.fulfilled, (state, action) => {
				Object.assign(state, action.payload);
			});

			builder.addCase(saveNote.pending, (state, action) => {
				state.savingNoteId = action.meta.arg.id;
			});
			builder.addCase(saveNote.fulfilled, (state, action) => {
				state.editingNoteId = null;
				state.savingNoteId = null;

				const newText = action.meta.arg.text;

				state.notes = state.notes.map(note =>
					note.id === action.meta.arg.id
						? {
								...note,
								text: newText
							}
						: note
				);
			});

			builder.addCase(saveNote.rejected, state => {
				state.savingNoteId = null;
			});

			builder.addCase(removeNote.fulfilled, (state, action) => {
				state.notes = state.notes.filter(
					note => note.id !== action.meta.arg.id
				);
			});

			builder.addCase(fetchNotes.pending, state => {
				state.fetchingNotes = true;
			});

			builder.addCase(fetchNotes.fulfilled, (state, action) => {
				state.fetchingNotes = false;
				state.notes = action.payload;
			});

			builder.addCase(fetchNotes.rejected, state => {
				state.fetchingNotes = false;
			});

			builder.addMatcher(
				isAsyncThunkActionWithPrefix('editor'),
				handleAsyncThunkActionState()
			);
		}
	},
	initialState
);

export const {
	init,
	clear,
	resetCompressedFile,
	setCompressedFile,
	setIsModalEditNameOpen,
	setIsToolsUntouched,
	setLastSaved,
	setOriginalFileName,
	setPageNumber,
	setScale,
	setProgress,
	setPdfUrl,
	setAnnotationEditMode,
	setAnnotationEditorParam,
	setAnnotationEditorParams,
	setCursorTool,
	setSidebarOptionDisabled,
	reset,
	setAnnotationEditorStates,
	setShareModalOpen,
	setShareModalType,
	setAnnotationEditorSelected,
	setIsModalCompressOpen,
	setProtectModalOpen,
	setPassword,
	setSignatureModalOpen,
	setSignatureFontsLoaded,
	setPageOrder,
	setPagesModalOpen,
	putNewEmptyNote,
	removeNewEmptyNote,
	addNote,
	removeNoteFromStore,
	setNoteSearchTerm,
	setNoteForEdition,
	undoEditingNote,
	setClickedNoteInPage,
	setAddNoteMobileMode,
	setClickedNewNoteInPage,
	switchClickedNewNoteInPage,
	resetMobileNoteState,
	setCustomMode,
	setJoinModalOpen,
	setSaveAlert,
	setHasText,
	addEmbeddedFont,
	setTriggerAction,
	setRecoveryId,
	setAllFontsLoaded
} = editorSlice.actions;

export default editorSlice.reducer;

export const CustomMode = {
	FIND: 49,
	NOTES: 50
};
